import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { useOrganization } from "@/contexts/OrganizationContext";

const Settings = () => {
  const { organization } = useOrganization();
  const [magisteriumKey, setMagisteriumKey] = useState("");
  const [openAIKey, setOpenAIKey] = useState("");
  const [heroVideoUrl, setHeroVideoUrl] = useState("");
  const [heroVideoAutoplay, setHeroVideoAutoplay] = useState(true);
  const [heroVideoMuted, setHeroVideoMuted] = useState(true);
  const [heroVideoLoop, setHeroVideoLoop] = useState(true);
  const [heroVideoControls, setHeroVideoControls] = useState(false);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    checkAdminAndFetchSettings();
  }, [organization?.id]);

  const checkAdminAndFetchSettings = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data: roles } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .eq("role", "admin")
          .maybeSingle();
        
        if (roles) {
          setIsAdmin(true);
          await fetchSettings();
        } else {
          setIsAdmin(false);
          setFetching(false);
        }
      } else {
        setFetching(false);
      }
    } catch (error) {
      setFetching(false);
    }
  };

  const fetchSettings = async () => {
    if (!organization?.id) return;
    
    try {
      const { data, error } = await supabase
        .from("settings")
        .select("key, value")
        .eq("organization_id", organization.id)
        .in("key", [
          "MAGISTERIUM_API_KEY", 
          "OPENAI_API_KEY",
          "HERO_VIDEO_URL",
          "HERO_VIDEO_AUTOPLAY",
          "HERO_VIDEO_MUTED",
          "HERO_VIDEO_LOOP",
          "HERO_VIDEO_CONTROLS"
        ]);

      if (error) throw error;
      
      data?.forEach((setting) => {
        if (setting.key === "MAGISTERIUM_API_KEY") setMagisteriumKey(setting.value);
        if (setting.key === "OPENAI_API_KEY") setOpenAIKey(setting.value);
        if (setting.key === "HERO_VIDEO_URL") setHeroVideoUrl(setting.value);
        if (setting.key === "HERO_VIDEO_AUTOPLAY") setHeroVideoAutoplay(setting.value === "true");
        if (setting.key === "HERO_VIDEO_MUTED") setHeroVideoMuted(setting.value === "true");
        if (setting.key === "HERO_VIDEO_LOOP") setHeroVideoLoop(setting.value === "true");
        if (setting.key === "HERO_VIDEO_CONTROLS") setHeroVideoControls(setting.value === "true");
      });
    } catch (error: any) {
      toast.error("Failed to load settings");
    } finally {
      setFetching(false);
    }
  };

  const saveSetting = async (key: string, value: string) => {
    if (!organization?.id) return;
    
    const { data: existing } = await supabase
      .from("settings")
      .select("id")
      .eq("key", key)
      .eq("organization_id", organization.id)
      .single();

    if (existing) {
      const { error } = await supabase
        .from("settings")
        .update({ value, updated_at: new Date().toISOString() })
        .eq("key", key)
        .eq("organization_id", organization.id);
      if (error) throw error;
    } else {
      const { error } = await supabase
        .from("settings")
        .insert({ key, value, organization_id: organization.id });
      if (error) throw error;
    }
  };

  const handleSaveApiKeys = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await saveSetting("MAGISTERIUM_API_KEY", magisteriumKey);
      await saveSetting("OPENAI_API_KEY", openAIKey);
      toast.success("API settings saved successfully");
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVideoSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await saveSetting("HERO_VIDEO_URL", heroVideoUrl);
      await saveSetting("HERO_VIDEO_AUTOPLAY", heroVideoAutoplay.toString());
      await saveSetting("HERO_VIDEO_MUTED", heroVideoMuted.toString());
      await saveSetting("HERO_VIDEO_LOOP", heroVideoLoop.toString());
      await saveSetting("HERO_VIDEO_CONTROLS", heroVideoControls.toString());
      toast.success("Video settings saved successfully");
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return <div className="p-8">Loading...</div>;
  }

  if (!isAdmin) {
    return (
      <div className="p-8">
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
          <p className="text-muted-foreground">
            Only administrators can access settings.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-2xl space-y-8">
      <h1 className="text-3xl font-bold">Settings</h1>
      
      {/* Hero Video Settings */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">Hero Video Settings</h2>
        <form onSubmit={handleSaveVideoSettings} className="space-y-6">
          <div>
            <Label htmlFor="hero-video-url">YouTube Video URL</Label>
            <Input
              id="hero-video-url"
              type="text"
              value={heroVideoUrl}
              onChange={(e) => setHeroVideoUrl(e.target.value)}
              placeholder="https://youtu.be/CpjGwzS31wg or https://www.youtube.com/watch?v=..."
            />
            <p className="text-sm text-muted-foreground mt-1">
              The video displayed in the hero section on the homepage
            </p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="hero-video-autoplay">Autoplay</Label>
                <p className="text-sm text-muted-foreground">
                  Start playing automatically on page load
                </p>
              </div>
              <Switch
                id="hero-video-autoplay"
                checked={heroVideoAutoplay}
                onCheckedChange={setHeroVideoAutoplay}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="hero-video-muted">Muted</Label>
                <p className="text-sm text-muted-foreground">
                  Play without sound (required for autoplay)
                </p>
              </div>
              <Switch
                id="hero-video-muted"
                checked={heroVideoMuted}
                onCheckedChange={setHeroVideoMuted}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="hero-video-loop">Loop</Label>
                <p className="text-sm text-muted-foreground">
                  Restart video when it ends
                </p>
              </div>
              <Switch
                id="hero-video-loop"
                checked={heroVideoLoop}
                onCheckedChange={setHeroVideoLoop}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="hero-video-controls">Show Controls</Label>
                <p className="text-sm text-muted-foreground">
                  Display video player controls
                </p>
              </div>
              <Switch
                id="hero-video-controls"
                checked={heroVideoControls}
                onCheckedChange={setHeroVideoControls}
              />
            </div>
          </div>
          
          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save Video Settings"}
          </Button>
        </form>
      </Card>

      {/* API Settings */}
      <Card className="p-6">
        <h2 className="text-xl font-semibold mb-4">API Settings</h2>
        <form onSubmit={handleSaveApiKeys} className="space-y-6">
          <div>
            <Label htmlFor="magisterium-key">Magisterium API Key</Label>
            <Input
              id="magisterium-key"
              type="password"
              value={magisteriumKey}
              onChange={(e) => setMagisteriumKey(e.target.value)}
              placeholder="Enter your Magisterium API key"
            />
            <p className="text-sm text-muted-foreground mt-1">
              Used for Catholic theological questions in the AI assistant
            </p>
          </div>
          
          <div>
            <Label htmlFor="openai-key">OpenAI API Key</Label>
            <Input
              id="openai-key"
              type="password"
              value={openAIKey}
              onChange={(e) => setOpenAIKey(e.target.value)}
              placeholder="Enter your OpenAI API key"
            />
            <p className="text-sm text-muted-foreground mt-1">
              Used for generating vector embeddings for feature request similarity search
            </p>
          </div>
          
          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : "Save API Settings"}
          </Button>
        </form>
      </Card>
    </div>
  );
};

export default Settings;
